import { RadioGroup } from "@headlessui/react";
import { Switch } from "@headlessui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as settingsApi from "../api";
import toast from "react-hot-toast";
import { useUserPreferences } from "../hooks/useUserPreferences";
import { useWatchlistAlertMode, WatchlistAlertMode } from "../hooks/useWatchlistAlertMode";
import { playChime, playSpoon, playBeep } from "@/utils/sound-utils";

function SoundTestButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="w-4 h-4"
      >
        <path d="M10 3.75a.75.75 0 0 0-1.264-.546L4.703 7H3.167a.75.75 0 0 0-.7.48A6.985 6.985 0 0 0 2 10c0 .887.165 1.737.468 2.52.111.29.39.48.7.48h1.535l4.033 3.796A.75.75 0 0 0 10 16.25V3.75ZM15.95 5.05a.75.75 0 0 0-1.06 1.061 5.5 5.5 0 0 1 0 7.778.75.75 0 0 0 1.06 1.06 7 7 0 0 0 0-9.899Z" />
        <path d="M13.829 7.172a.75.75 0 0 0-1.061 1.06 2.5 2.5 0 0 1 0 3.536.75.75 0 0 0 1.06 1.06 4 4 0 0 0 0-5.656Z" />
      </svg>
      Test
    </button>
  );
}

const alertModeOptions: { value: WatchlistAlertMode; label: string; description: string }[] = [
  { value: "off", label: "Off", description: "No watchlist sound alerts" },
  { value: "highlight", label: "Highlight only", description: "Alert when items match highlighted watchlists" },
  { value: "filter", label: "Filter only", description: "Alert when items match filtered watchlists" },
  { value: "both", label: "Both", description: "Alert for both filter and highlight" },
];

function Notifications() {
  const { notificationsSound } = useUserPreferences();
  const { watchlistAlertMode, setWatchlistAlertMode } = useWatchlistAlertMode();

  const queryClient = useQueryClient();

  const toggleSoundMutation = useMutation({
    mutationFn: settingsApi.toggleNotificationsSound,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
    },

    onError: () =>
      toast.error(
        "An error occured while updating user prefernces, please try again."
      ),
  });

  return (
    <div className="divide-y divide-white/10">
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Headlines Notification Sound
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Enable/Disable sound notifications for new headlines received.
          </p>
        </div>
        <div className="self-center flex items-center gap-4">
          <Switch
            as="div"
            checked={notificationsSound.headlines}
            onChange={() =>
              toggleSoundMutation.mutate({
                headlines: !notificationsSound.headlines,
                truth_social: notificationsSound.truth_social,
              })
            }
            className="group relative inline-flex w-12 p-1 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue focus-visible:ring-offset-2 data-[checked]:bg-squawk-blue"
          >
            <span className="sr-only">Enable/Disable notification sound</span>
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
            />
          </Switch>
          <SoundTestButton onClick={() => playChime()} />
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Truth Social Notification Sound
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Enable/Disable sound notifications for new truth social stories
            received.
          </p>
        </div>
        <div className="self-center flex items-center gap-4">
          <Switch
            as="div"
            checked={notificationsSound.truth_social}
            onChange={() =>
              toggleSoundMutation.mutate({
                headlines: notificationsSound.headlines,
                truth_social: !notificationsSound.truth_social,
              })
            }
            className="group relative inline-flex w-12 p-1 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue focus-visible:ring-offset-2 data-[checked]:bg-squawk-blue"
          >
            <span className="sr-only">Enable/Disable notification sound</span>
            <span
              aria-hidden="true"
              className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
            />
          </Switch>
          <SoundTestButton onClick={() => playBeep()} />
        </div>
      </div>

      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Watchlist Alert Sound Mode
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Configure when watchlist sound alerts are triggered. Requires sound
            to be enabled on individual watchlists.
          </p>
          <div className="mt-3">
            <SoundTestButton onClick={() => playSpoon()} />
          </div>
        </div>
        <div className="self-center md:col-span-2">
          <RadioGroup
            value={watchlistAlertMode}
            onChange={(value: WatchlistAlertMode) => setWatchlistAlertMode(value)}
            className="space-y-2"
          >
            {alertModeOptions.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option.value}
                className={({ checked }) =>
                  `relative flex cursor-pointer rounded-lg px-4 py-3 focus:outline-none ${
                    checked
                      ? "bg-squawk-blue/20 border border-squawk-blue"
                      : "bg-white/5 border border-white/10 hover:bg-white/10"
                  }`
                }
              >
                {({ checked }) => (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <RadioGroup.Label
                          as="p"
                          className={`font-medium ${
                            checked ? "text-white" : "text-gray-300"
                          }`}
                        >
                          {option.label}
                          {option.value === "off" && (
                            <span className="ml-2 text-xs text-gray-500">
                              (Default)
                            </span>
                          )}
                        </RadioGroup.Label>
                        <RadioGroup.Description
                          as="span"
                          className="text-gray-500"
                        >
                          {option.description}
                        </RadioGroup.Description>
                      </div>
                    </div>
                    {checked && (
                      <div className="shrink-0 text-squawk-blue">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        </div>
      </div>
    </div>
  );
}

export default Notifications;
